import { FillInTheBlankQuestion } from '../Models/content-types/fill_in_the_blank_question';
import { Elements } from '@kontent-ai/delivery-sdk';

export class QuizQuestion {
  questionText: string = '';
  options: string[] = [];
  correctAnswer: string = '';
  correctFeedback: Elements.RichTextElement = {
    type: 'rich_text',
    value: '',
  } as Elements.RichTextElement;
  questionType: string = '';
  questionId: string = '';
  originalKontentItem?: any;
  errors: string[] = []; // Store any errors encountered during transformation

  transformQuestionsFromKontent(kontentQuizQuestion: any): void {
    // Reset errors array
    this.errors = [];

    // Check if kontentQuizQuestion is null or undefined
    if (!kontentQuizQuestion) {
      this.errors.push('Quiz question data is null or undefined');
      return;
    }

    // Check if required properties exist
    if (!kontentQuizQuestion.system) {
      this.errors.push('Missing system property in quiz question data');
      return;
    }

    if (!kontentQuizQuestion.elements) {
      this.errors.push('Missing elements property in quiz question data');
      return;
    }

    try {
      this.questionId = kontentQuizQuestion.system.id || '';

      // Safe access to question value
      if (!kontentQuizQuestion.elements.question?.value) {
        this.errors.push('Missing question value');
      } else {
        this.questionText = kontentQuizQuestion.elements.question.value;
      }

      this.questionType = kontentQuizQuestion.system.type || ''; // Safe access to feedback elements - correct_answer_feedback is optional
      this.correctFeedback = kontentQuizQuestion.elements
        .correct_answer_feedback
        ? (kontentQuizQuestion.elements
            .correct_answer_feedback as Elements.RichTextElement)
        : ({
            type: 'rich_text',
            value: '',
          } as Elements.RichTextElement);
      this.originalKontentItem = kontentQuizQuestion;
      switch (kontentQuizQuestion.system.type) {
        case 'multiple_choice_question':
          if (!kontentQuizQuestion.elements.potential_answers?.linkedItems) {
            this.errors.push(
              'Missing potential answers for multiple choice question',
            );
          } else {
            kontentQuizQuestion.elements.potential_answers.linkedItems.forEach(
              (answer: any) => {
                if (!answer?.elements?.potential_answer?.value) {
                  this.errors.push('Invalid potential answer format');
                } else {
                  this.options.push(answer.elements.potential_answer.value);
                }
              },
            );
          }

          // Safe access to correct answer
          if (
            !kontentQuizQuestion.elements.correct_answer?.linkedItems?.length
          ) {
            this.errors.push(
              'Missing correct answer for multiple choice question',
            );
          } else {
            const correctAnswerItem =
              kontentQuizQuestion.elements.correct_answer.linkedItems[0];
            if (!correctAnswerItem?.elements?.potential_answer?.value) {
              this.errors.push('Invalid correct answer format');
            } else {
              this.correctAnswer =
                correctAnswerItem.elements.potential_answer.value;
            }
          }
          break;
        case 'fill_in_the_blank_question': {
          this.originalKontentItem =
            kontentQuizQuestion as FillInTheBlankQuestion;

          if (!kontentQuizQuestion.elements.potential_answers?.value) {
            this.errors.push(
              'Missing potential answers for fill in the blank question',
            );
          } else {
            let uncleanOptions =
              kontentQuizQuestion.elements.potential_answers.value.split(';');

            if (uncleanOptions.length === 0) {
              this.errors.push(
                'No potential answers provided for fill in the blank question',
              );
            }

            for (let option of uncleanOptions) {
              let cleanOption = option.trim();
              if (cleanOption !== '') {
                this.options.push(cleanOption);
              }
            }

            // Ensure we have at least one valid option
            if (this.options.length === 0) {
              this.errors.push(
                'No valid options found for fill in the blank question',
              );
            }
          }
          break;
        }

        default:
          this.errors.push(
            `Unsupported question type: ${kontentQuizQuestion.system.type}`,
          );
          break;
      }
    } catch (error) {
      this.errors.push(
        `Unexpected error: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    // Log errors if any
    if (this.errors.length > 0) {
      console.warn(
        'Errors encountered while transforming quiz question:',
        this.errors,
      );
    }
  }

  /**
   * Returns whether the question data is valid
   */
  isValid(): boolean {
    return this.errors.length === 0;
  }
}
